<script setup lang="ts">
  import pluralize from 'pluralize';
  import { computed, nextTick, ref, toRef, watch } from 'vue';

  import type { Email, EmailThread, Task } from '@stuut/api/models';
  import { stripHTML } from '@stuut/common/src/utils';
  import { Shimmer } from '@ui/components/ai-elements/shimmer';
  import { Badge, DraftBadge } from '@ui/components/badge';
  import { Button } from '@ui/components/button';
  import { HoverCard, HoverCardTrigger } from '@ui/components/hover-card';
  import { Separator } from '@ui/components/separator';
  import { toast } from '@ui/components/sonner';
  import { Timestamp } from '@ui/components/time';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { cn } from '@ui/utils/utils';

  import { stuut } from '@/api';
  import LinkEmailDialog from '@/components/email-senders/LinkEmailDialog.vue';
  import EmailDetailsPopover from '@/components/emails/EmailDetailsPopover.vue';
  import SuggestedReplyPopover from '@/components/emails/SuggestedReplyPopover.vue';
  import EmailTaskIndicator from '@/components/tasks/EmailTaskIndicator.vue';
  import { useEmailResponseTasks } from '@/composables/useEmailResponseTasks';
  import { useEmailSenders, useProfile } from '@/store';
  import { useFeatureFlags } from '@/store/featureFlags';

  interface Props {
    collapsed?: boolean;
    email: Email;
    hideReply?: boolean;
    processing?: boolean;
    size?: '2xs' | 'xs' | 'sm';
    thread: EmailThread;
  }

  const props = defineProps<Props>();

  const emits = defineEmits<{
    (e: 'draftCreated', draft: Email): void;
  }>();

  const isReplying = ref(false);
  const isForwarding = ref(false);

  const replyOptions: { icon: string; label: string; replyAll: boolean; spacing?: string }[] = [
    { icon: 'fa-reply', label: 'Reply', replyAll: false },
    { icon: 'fa-reply-all', label: 'Reply all', replyAll: true, spacing: 'ml-1.5' },
  ];

  const emailSenderStore = useEmailSenders();
  const emailSenders = computed(() => emailSenderStore.emailSenders);
  const linkEmailDialogRef = ref();

  const profileStore = useProfile();

  const featureFlagsStore = useFeatureFlags();
  const emailForwardingEnabled = computed(() => !!featureFlagsStore.flags['email-forwarding']);

  const parentEmail = computed<Email | null>(() => {
    if (props.email.parent_email_id) {
      return props.thread.getEmailById(props.email.parent_email_id);
    }

    return null;
  });

  // Draft (non-sent) replies addressed to this email — surfaced as a count when collapsed.
  const draftReplyCount = computed<number>(
    () =>
      props.thread.emails.filter((e) => !e.isSent && e.parent_email_id === props.email.id).length,
  );

  const canReply = computed(() => {
    return (
      emailSenders.value.length > 0 &&
      (!parentEmail.value ||
        emailSenders.value.some((emailSender) => parentEmail.value?.hasRemoteLink(emailSender)))
    );
  });

  const canForward = computed(() => {
    return (
      emailSenders.value.length > 0 &&
      emailSenders.value.some((emailSender) =>
        props.email.hasRemoteLinkInChain(emailSender, props.thread),
      )
    );
  });

  const onReplyAdded = (replyAll: boolean) => {
    if (isReplying.value) {
      return;
    }

    isReplying.value = true;

    stuut.emails
      .create({
        options: { reply_all: replyAll || false },
        parent_email: props.email.id,
      })
      .then((draftEmail) => {
        emits('draftCreated', draftEmail);
        toast.success('Draft reply has been generated.');
      })
      .catch((err) => {
        toast.error(err.clientMessage);
      })
      .finally(() => {
        isReplying.value = false;
      });
  };

  const onForwardAdded = () => {
    if (isForwarding.value) {
      return;
    }

    isForwarding.value = true;

    stuut.emails
      .forward(props.email.id)
      .then((draftEmail) => {
        emits('draftCreated', draftEmail);
        toast.success('Draft forward has been generated.');
      })
      .catch((err) => {
        toast.error(err.clientMessage);
      })
      .finally(() => {
        isForwarding.value = false;
      });
  };

  const textClassName = computed(() => {
    if (props.size) return `text-${props.size}`;
    return 'text-sm';
  });

  const senderName = computed<string>(() => {
    return (
      props.email.from_email ??
      (props.email.direction === 'inbound'
        ? props.thread.partner?.name
        : props.email.author?.name) ??
      'No Email'
    );
  });

  const plainSnippet = computed<string>(() => {
    const raw = props.email.snippet || props.email.body || '';
    return stripHTML(raw).replace(/\s+/g, ' ').trim();
  });

  const recipientLine = computed<string>(() => {
    const tos = props.email.to ?? [];
    if (!tos.length) return '';
    return `to ${tos.join(', ')}`;
  });

  const hasSuggestedReply = computed<boolean>(
    () => (props.email.openResponseTasks?.length ?? 0) > 0,
  );

  const { applyTaskAsDraft, fullResponseTasks, isApplying, loadResponseTasks } =
    useEmailResponseTasks(toRef(props, 'email'));

  watch(
    hasSuggestedReply,
    (has) => {
      if (has) loadResponseTasks();
    },
    { immediate: true },
  );

  const suggestionsSource = computed<Task[]>(() => {
    if (fullResponseTasks.value.length > 0) return fullResponseTasks.value;
    return (props.email.openResponseTasks ?? []) as Task[];
  });

  const visibleSuggestions = computed<Task[]>(() => suggestionsSource.value.slice(0, 3));
  const overflowCount = computed<number>(() =>
    Math.max(0, suggestionsSource.value.length - visibleSuggestions.value.length),
  );

  const handleUseTaskSuggestion = async (task: Task) => {
    const draft = await applyTaskAsDraft(task);
    if (draft) emits('draftCreated', draft);
  };

  const scrollToSuggestionPanel = () => {
    const el = document.querySelector(`[data-email-suggestion="${props.email.id}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToAttachments = (e: Event) => {
    if (!props.collapsed) {
      e.preventDefault();
      e.stopPropagation();
    }

    nextTick(() =>
      setTimeout(
        () => {
          const el = document.querySelector(`#${props.email.id}-attachments`);
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
        props.collapsed ? 250 : 0,
      ),
    );
  };
</script>

<template>
  <div class="flex flex-row w-full gap-4 items-center">
    <div
      :class="
        cn(
          'size-6 -mt-4.5 border flex items-center justify-center shrink-0 transition-transform',
          email.direction === 'inbound'
            ? 'bg-primary/10 text-primary border-primary/20'
            : 'bg-success/5 text-success border-success/20',
        )
      "
    >
      <i
        :class="
          email.direction === 'inbound'
            ? 'far fa-arrow-down-left text-xs'
            : 'far fa-arrow-up-right text-xs'
        "
      />
    </div>

    <div class="flex flex-col flex-1 min-w-0 gap-y-1.5 text-sm">
      <div class="flex flex-row items-center gap-2 w-full overflow-hidden">
        <slot name="after_direction_badge" />

        <EmailDetailsPopover
          :email
          :open-delay="0"
          trigger-class="flex justify-start items-center gap-x-2.5 min-w-0 max-w-full"
        >
          <template #trigger>
            <i
              class="fas text-xs shrink-0 text-muted-foreground"
              :class="collapsed ? 'fa-caret-down' : 'fa-caret-up'"
            />

            <Shimmer
              v-if="processing"
              as="span"
              :duration="1.5"
              class="truncate font-semibold"
              :class="textClassName"
            >
              {{ senderName }}
            </Shimmer>

            <span v-else class="truncate font-semibold" :class="textClassName">
              {{ senderName }}
            </span>

            <div v-if="email.isSending" class="ml-auto shrink-0">
              <Badge variant="open" size="xs">Sending</Badge>
            </div>
          </template>
        </EmailDetailsPopover>

        <EmailTaskIndicator :email="email" />

        <DraftBadge v-if="draftReplyCount > 0" :count="draftReplyCount" />

        <Tooltip v-if="email.attachments?.length" :open-delay="0" :close-delay="150">
          <TooltipTrigger as-child>
            <Button
              class="rounded-full"
              variant="ghost"
              size="icon"
              @click="(e) => scrollToAttachments(e)"
            >
              <i class="fas fa-paperclip"></i>
            </Button>
          </TooltipTrigger>

          <TooltipContent>{{
            pluralize('attachment', email.attachments?.length, true)
          }}</TooltipContent>
        </Tooltip>

        <slot name="header_right" />

        <div
          class="flex flex-row items-center justify-end gap-3 w-fit text-nowrap ml-auto shrink-0 h-7"
        >
          <Timestamp
            class="text-xs text-muted-foreground tracking-wide"
            :time="email.sent_at"
            local
            format="MMM D YYYY h:mmA"
            show-from-now
          />

          <Separator v-if="!hideReply" orientation="vertical" class="h-4" />

          <div v-if="!hideReply" class="flex gap-0.5">
            <template v-for="opt in replyOptions" :key="opt.icon">
              <HoverCard v-if="hasSuggestedReply && canReply" :open-delay="100" :close-delay="150">
                <HoverCardTrigger as-child>
                  <Button
                    variant="outline"
                    size="icon-xs"
                    :class="
                      cn(
                        'relative border-amber-300/60 dark:border-amber-700/40 bg-amber-50/40 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300 hover:bg-amber-100/60 dark:hover:bg-amber-950/40',
                        opt.spacing,
                      )
                    "
                    :disabled="!canReply || isReplying"
                    @click.stop="onReplyAdded(opt.replyAll)"
                  >
                    <i :class="`fa far ${opt.icon}`" />
                  </Button>
                </HoverCardTrigger>

                <SuggestedReplyPopover
                  :visible-suggestions="visibleSuggestions"
                  :overflow-count="overflowCount"
                  :total-count="suggestionsSource.length"
                  :is-applying="isApplying"
                  :is-blank-reply-disabled="!canReply || isReplying"
                  @use="handleUseTaskSuggestion"
                  @blank-reply="onReplyAdded(opt.replyAll)"
                  @scroll-to-more="scrollToSuggestionPanel"
                />
              </HoverCard>

              <Tooltip v-else as-child>
                <TooltipTrigger as-child>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    :class="opt.spacing"
                    :disabled="!canReply || isReplying"
                    @click.stop="onReplyAdded(opt.replyAll)"
                  >
                    <i :class="`fa far ${opt.icon}`" />
                  </Button>
                </TooltipTrigger>

                <TooltipContent>
                  <template v-if="canReply"> {{ opt.label }} </template>

                  <p v-else-if="!emailSenders.length">
                    <template v-if="profileStore.profile.canLinkEmail">
                      You must
                      <Button variant="link" size="inline" @click.stop="linkEmailDialogRef.open()">
                        link an email
                      </Button>
                      before you can reply.
                    </template>
                    <template v-else>
                      No email senders configured. Contact your Stuut representative.
                    </template>
                  </p>

                  <p v-else>
                    You cannot reply to this email because you do not have access to an email
                    account that is a participant in this email.
                  </p>
                </TooltipContent>
              </Tooltip>
            </template>

            <Tooltip v-if="emailForwardingEnabled" as-child>
              <TooltipTrigger as-child>
                <Button
                  variant="ghost"
                  class="px-2 gap-x-2.5"
                  size="icon-xs"
                  :disabled="!canForward || isForwarding"
                  @click.stop="onForwardAdded()"
                >
                  <i class="far fa-share" />
                </Button>
              </TooltipTrigger>

              <TooltipContent>
                <template v-if="canForward"> Forward </template>

                <p v-else-if="!emailSenders.length">
                  <template v-if="profileStore.profile.canLinkEmail">
                    You must
                    <Button variant="link" size="inline" @click.stop="linkEmailDialogRef.open()">
                      link an email
                    </Button>
                    before you can forward.
                  </template>
                  <template v-else>
                    No email senders configured. Contact your Stuut representative.
                  </template>
                </p>

                <p v-else>
                  You cannot forward this email because you do not have access to an email account
                  that received it.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>

      <div
        v-if="collapsed"
        class="text-muted-foreground truncate text-xs w-full"
        :class="textClassName"
      >
        {{ plainSnippet }}
      </div>

      <div v-else class="text-muted-foreground truncate text-xs w-full tracking-wide">
        <template v-if="recipientLine">
          <EmailDetailsPopover :email :open-delay="0" trigger-class="truncate max-w-full min-w-0">
            <template #trigger>
              {{ recipientLine }}
            </template>
          </EmailDetailsPopover>
        </template>

        <template v-else>&nbsp;</template>
      </div>
    </div>

    <LinkEmailDialog ref="linkEmailDialogRef" />
  </div>
</template>

<style scoped lang="postcss"></style>
